import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { FaPlus } from "react-icons/fa6";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { Formik, Form } from "formik";
import Select from "react-select";
import { TableLoader } from "../../../Common/ShimmerLoader";
import MUITable from "../../../Common/MUITable";
import isGHMC from "../../../Common/helper/helpar";
import {
  getAllComplaintZones,
  getComplaintCirclesByZoneId,
} from "../../../Apis/complaintApi";
import { ExportSlaUserMatrix } from "./ExportSlaUserMatrix";
import AddEditSlaUserMatrixForm from "./AddEditSlaUserMatrixForm";
import {
  DEFAULT_REPORT_ZONE_ID,
  DEFAULT_REPORT_ZONE_NAME,
  useSlaUserMatrixStore,
} from "./store/useSlaUserMatrixStore";
import { StatusBadge } from "../../Rfid/utils/statusBadge";

const SlaUserMatrixRootComponent = () => {
  const isProd = isGHMC();
  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [zones, setZones] = useState([]);
  const [wards, setWards] = useState([]);
  const [resolvedDefaultZoneId, setResolvedDefaultZoneId] = useState(
    DEFAULT_REPORT_ZONE_ID
  );

  const {
    filteredSlaUserMatrixList,
    isLoading,
    pagination,
    filters,
    fetchSlaUserMatrixList,
    fetchMasterOptions,
    updateFilter,
    resetFilters,
  } = useSlaUserMatrixStore();

  useEffect(() => {
    const init = async () => {
      let zoneId = filters.zoneId || DEFAULT_REPORT_ZONE_ID;

      try {
        const zoneResponse = await getAllComplaintZones();
        const activeZones = zoneResponse.filter((zone) => zone.isActive !== false);
        setZones(activeZones);

        const defaultZone =
          activeZones.find((zone) => zone.zoneName === DEFAULT_REPORT_ZONE_NAME) ||
          activeZones.find(
            (zone) => String(zone.zoneId) === DEFAULT_REPORT_ZONE_ID
          );
        zoneId = String(defaultZone?.zoneId ?? DEFAULT_REPORT_ZONE_ID);
        setResolvedDefaultZoneId(zoneId);
        updateFilter("zoneId", zoneId);

        await loadWards(zoneId);
      } catch (error) {
        console.error("Error fetching zones:", error);
        await loadWards(zoneId);
      }

      fetchMasterOptions();
      fetchSlaUserMatrixList({ zoneId, currentPage: 1 });
    };

    init();
  }, []);

  const loadWards = async (zoneId) => {
    if (!zoneId) {
      setWards([]);
      return;
    }
    try {
      const response = await getComplaintCirclesByZoneId(zoneId);
      setWards(response.filter((circle) => circle.isActive !== false));
    } catch (error) {
      console.error("Error fetching wards:", error);
      setWards([]);
    }
  };

  const handleEdit = (row) => {
    setIsEditMode(true);
    setEditRow(row);
    setShowModal(true);
  };

  const handleAdd = () => {
    setIsEditMode(false);
    setEditRow(null);
    setShowModal(true);
  };

  const handlePageChange = (_event, newPage) => {
    fetchSlaUserMatrixList({
      currentPage: newPage + 1,
    });
  };

  const handleRowsPerPageChange = (event) => {
    const nextPageSize = parseInt(event.target.value, 10);
    fetchSlaUserMatrixList({
      currentPage: 1,
      pageSize: nextPageSize,
    });
  };

  const initialFilterValues = {
    searchTerm: filters.searchTerm || "",
    zoneId: filters.zoneId || resolvedDefaultZoneId || DEFAULT_REPORT_ZONE_ID,
    circleId: filters.circleId || "",
  };

  const slaUserMatrixColumns = [
    { label: "Zone", field: "zoneName", width: "180px" },
    {
      label: "Ward No",
      width: "300px",
      render: (row) => row.wardNo || row.wardName || "N/A",
    },
    { label: "Name", field: "userName" },
    { label: "Mobile No", field: "mobileNo" },
    { label: "Login ID", field: "loginId" },
    { label: "Level", field: "level" },
    {
      label: "Alert Before Escalation",
      render: (row) =>
        row.alertBeforeEscalationMin
          ? `${row.alertBeforeEscalationMin} Minutes`
          : "N/A",
    },
    {
      label: "Escalation Duration",
      render: (row) =>
        row.escalationDurationMin ? `${row.escalationDurationMin} Minutes` : "N/A",
    },
    {
      label: "Status",
      render: (row) => <StatusBadge isActive={row.isActive} compact />,
    },
    {
      label: "Actions",
      render: (row) => (
        <button
          type="button"
          className={isProd ? "edit-ghmc" : "edit-swm"}
          onClick={() => handleEdit(row)}
          aria-label={`Edit ${row.userName}`}
        >
          <FontAwesomeIcon icon={faEdit} />
        </button>
      ),
    },
  ];

  return (
    <>
      <div className="content text-start">
        <div className="page-content">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mt-3">
            <div>
              <h5 className="mb-1 fw-semibold">SLA User Matrix Information</h5>
              <p className="text-muted mb-0">
                Configure escalation matrix and notification receivers for effective
                SLA management.
              </p>
            </div>
            <div className="d-flex align-items-center flex-wrap">
              <button type="button" className="button-container" onClick={handleAdd}>
                <FaPlus className="font-size-20 me-1" /> Add SLA User Matrix
              </button>
              <ExportSlaUserMatrix slaUserMatrixData={filteredSlaUserMatrixList} />
            </div>
          </div>

          <div className="card shadow mt-3">
            <div className="card-body">
              <Formik
                initialValues={initialFilterValues}
                enableReinitialize
                onSubmit={(values) => {
                  fetchSlaUserMatrixList({
                    currentPage: 1,
                    searchTerm: values.searchTerm,
                    zoneId: values.zoneId,
                    circleId: values.circleId,
                  });
                }}
              >
                {({ values, setFieldValue, resetForm, handleSubmit }) => (
                  <>
                    <Form onSubmit={handleSubmit}>
                      <div className="row align-items-end">
                        <div className="col-md-3 mb-3">
                          <label className="form-label">Search</label>
                          <input
                            type="text"
                            name="searchTerm"
                            value={values.searchTerm}
                            onChange={(e) =>
                              setFieldValue("searchTerm", e.target.value)
                            }
                            placeholder="Enter User Name / Login ID"
                            className="form-control bg-white"
                          />
                        </div>

                        <div className="col-md-3 mb-3">
                          <label className="form-label">Zone</label>
                          <select
                            name="zoneId"
                            value={
                              values.zoneId ||
                              resolvedDefaultZoneId ||
                              DEFAULT_REPORT_ZONE_ID
                            }
                            onChange={(e) => {
                              const zoneId = e.target.value;
                              setFieldValue("zoneId", zoneId);
                              setFieldValue("circleId", "");
                              loadWards(zoneId);
                            }}
                            className="form-select bg-white"
                          >
                            {zones.length > 0 ? (
                              zones.map((zone) => (
                                <option
                                  key={zone.zoneId}
                                  value={String(zone.zoneId)}
                                >
                                  {zone.zoneName}
                                </option>
                              ))
                            ) : (
                              <option value={DEFAULT_REPORT_ZONE_ID}>
                                {DEFAULT_REPORT_ZONE_NAME}
                              </option>
                            )}
                          </select>
                        </div>

                        <div className="col-md-3 mb-3">
                          <label className="form-label">Ward</label>
                          <Select
                            inputId="circleId"
                            name="circleId"
                            classNamePrefix="react-select"
                            options={wards.map((ward) => ({
                              value: String(ward.circleId),
                              label: ward.circleName,
                            }))}
                            value={
                              wards
                                .map((ward) => ({
                                  value: String(ward.circleId),
                                  label: ward.circleName,
                                }))
                                .find(
                                  (option) => option.value === String(values.circleId)
                                ) || null
                            }
                            onChange={(selected) =>
                              setFieldValue("circleId", selected ? selected.value : "")
                            }
                            placeholder="Select Ward"
                            isClearable={false}
                            menuPlacement="auto"
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                          />
                        </div>

                        <div className="col-md-12 mb-3 d-flex gap-2">
                          <button
                            type="submit"
                            style={{
                              backgroundColor: "#003366",
                              color: "#FFFFFF",
                              border: "none",
                              fontWeight: 600,
                              padding: "8px 24px",
                              borderRadius: "6px",
                              fontSize: "14px",
                            }}
                          >
                            APPLY FILTERS
                          </button>
                          <button
                            type="button"
                            style={{
                              backgroundColor: "#FDE8E8",
                              color: "#8B1A1A",
                              border: "none",
                              fontWeight: 600,
                              padding: "8px 24px",
                              borderRadius: "6px",
                              fontSize: "14px",
                            }}
                            onClick={() => {
                              setResolvedDefaultZoneId(DEFAULT_REPORT_ZONE_ID);
                              resetForm();
                              loadWards(DEFAULT_REPORT_ZONE_ID);
                              resetFilters();
                            }}
                          >
                            RESET
                          </button>
                        </div>
                      </div>
                    </Form>

                    <div>
                      <div className="top-header text-center">
                        <span>SLA User Matrix List</span>
                      </div>
                      <Box
                        sx={{
                          "& .MuiTableBody-root .MuiTableCell-root": {
                            padding: "4px 10px",
                          },
                        }}
                      >
                        <MUITable loading={isLoading}
                          needSRNumber
                          rows={filteredSlaUserMatrixList}
                          columns={slaUserMatrixColumns}
                          serverPagination
                          totalCount={pagination.totalCount}
                          page={Math.max(0, pagination.currentPage - 1)}
                          rowsPerPage={pagination.pageSize}
                          onPageChange={handlePageChange}
                          onRowsPerPageChange={handleRowsPerPageChange}
                          rowsPerPageOptions={[10, 100, 10000, 50000]}
                        />
                      </Box>
                    </div>
                  </>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>

      <AddEditSlaUserMatrixForm
        showModal={showModal}
        setShowModal={setShowModal}
        editRow={editRow}
        isEditMode={isEditMode}
      />
    </>
  );
};

export default SlaUserMatrixRootComponent;
